

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface ingredients {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    // ingredients: ingredients;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const womenhealth: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "womens-health-services",
        "meta": {
            "title": "Women's Health Services in Dubai | Elite Body Home",
            "description": "Comprehensive women's health services including gynecological care, fertility treatments, hormonal therapy, and preventive screenings in Dubai.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/womens-health/",
            "og": {
                "title": "Women's Health Services in Dubai | Elite Body Home",
                "description": "Holistic healthcare solutions for women at all life stages",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Womens-Health-Services-Dubai.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Women's Health"],
        "hero": {
            "title": "Women's Health Services in Dubai",
            "subtitle": "Comprehensive Care for Every Stage of Life",
            "description": "Women's health is an extremely important branch of medicine that specializes in conditions exclusively affecting women, and impacting their physical, emotional, and reproductive health. Dubai is at the cusp of offering top-notch women health services that uses a holistic approach to health care."
        },
        "howItWorks": {
            "title": "What Are Women's Health Services?",
            "description": "Women's health in Dubai is treated in a patient-centered form of practice which means its planning for care is individualized and related to issues due to different stages in a woman's life."
        },
        "procedureSteps": {
            "heading": "Women Health Services Procedure",
            "para": "Generally, the steps to access women's health services are as follows:",
            "steps": [
                {
                    "title": "Initial Consultation",
                    "desc": "A health care specialist reviews your comprehensive medical history & health concerns for the first visit.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Screening and Diagnostic Tests",
                    "desc": "These include blood tests, ultrasound imaging, PAP smears, mammograms and any other necessary exams.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "Treatment Plan",
                    "desc": "An individualized treatment plan is designed according to the diagnosis. It can involve medications or lifestyle adjustments, as well as procedures such as hormone replacement therapy and laparoscopic surgeries.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Follow-Up",
                    "desc": "The follow up is done post treatment to see how well the treatment administered has worked and monitor improvement overall.",
                    "icon": "fa-solid fa-heartbeat"
                }
            ]
        },
        "benefits": {
            "heading": "Why You Should Care About Women's Health Services in Dubai",
            "subheading": "",
            "para": "The women health services offers various benefits that help you stay physically and emotionally fit.",
            "items": [
                {
                    "title": "Reduced Fatigue",
                    "desc": "Use of hormonal therapies or treatments have been shown to increase energy levels and reduce fatigue.",
                    "icon": "fa-solid fa-battery-full"
                },
                {
                    "title": "Hormonal Restoration",
                    "desc": "HRT and targeted therapies restore hormonal balance; this reverses symptoms of menopause, PCOS, etc.",
                    "icon": "fa-solid fa-balance-scale"
                },
                {
                    "title": "Weight Reduction",
                    "desc": "Progressive programs allow for the management of appetite and successful weight decrease.",
                    "icon": "fa-solid fa-weight"
                },
                {
                    "title": "Improved Chance of Conceiving",
                    "desc": "Fertility treatments can significantly increase the chances of becoming pregnant.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "Fairer Skin",
                    "desc": "Certain therapies make the skin look youthful, reduce pimples and rejuvenate appearance.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Psychological Health",
                    "desc": "Personalized counselling and treatments promote mental well being and aid in dealing with stress, anxiety and depression.",
                    "icon": "fa-solid fa-brain"
                }
            ]
        },
        "eligibility": {
            "heading": "Women Health Services in Dubai Eligibility",
            "subheading": "",
            "para": "Services are provided for the benefit of the widest possible group, therefore inclusive and accessible in nature.",
            "items": [
                {
                    "title": "Menstrual Irregularities",
                    "desc": "Women experiencing irregular menses or severe symptoms of premenstrual syndrome (PMS).",
                    "icon": "fa-solid fa-calendar-days"
                },
                {
                    "title": "Fertility Concerns",
                    "desc": "Women who intend to get pregnant or have difficulties becoming pregnant.",
                    "icon": "fa-solid fa-venus"
                },
                {
                    "title": "Menopause",
                    "desc": "Women approaching or going through the menopause.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "Preventive Care",
                    "desc": "Women who need routine examinations and preventive care.",
                    "icon": "fa-solid fa-shield-heart"
                },
                {
                    "title": "Hormonal Imbalance",
                    "desc": "Individuals needing to treat hormone imbalance or manage their weight.",
                    "icon": "fa-solid fa-atom"
                },
                {
                    "title": "Pregnancy Care",
                    "desc": "Care tailored for pregnant women and new mothers promotes a healthy pregnancy and beyond.",
                    "icon": "fa-solid fa-person-pregnant"
                }
            ]
        },
        "preparationTips": {
            "heading": "Before You Come to Women Health Services",
            "subheading": "",
            "para": "If you've been putting off women's health treatments or screenings, preparation is as easy as this:",
            "items": [
                {
                    "title": "Schedule Appointments",
                    "desc": "Make appointments ahead of time. Consult a good clinic in Dubai and get an appointment at the earliest possible.",
                    "icon": "fa-solid fa-calendar-plus"
                },
                {
                    "title": "Medication History",
                    "desc": "Prepare a list of your health history, medications, allergies and past surgeries.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Before the Test",
                    "desc": "If you are getting diagnosis tests, you may be asked to be fasting or to not take certain drugs before the test.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "Clothing",
                    "desc": "Wear comfortable clothes, depending on what procedures are going to be performed, like ultrasounds or gynecological exams.",
                    "icon": "fa-solid fa-shirt"
                }
            ]
        },
        "results": {
            "heading": "Outcomes of Women Health Services",
            "subheading": "",
            "para": "These are some of the amazing advantages of availing women's health services in Dubai as it can transform your life entirely.",
            "items": [
                {
                    "title": "Physical Fitness",
                    "desc": "Weight loss programs promote a fit lifestyle and prevent people from chronic diseases.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "Hormonal Balance",
                    "desc": "Hormonal treatments remove the symptoms of mood locomotion, flush, or tiredness.",
                    "icon": "fa-solid fa-scale-balanced"
                },
                {
                    "title": "Prevention of Health Problems",
                    "desc": "Regular check-ups and screenings can identify potential health issues, enabling early treatment.",
                    "icon": "fa-solid fa-magnifying-glass"
                },
                {
                    "title": "Enhanced Reproductive Health",
                    "desc": "Fertility treatments enhance the probability of conception and result in healthier pregnancies.",
                    "icon": "fa-solid fa-children"
                },
                {
                    "title": "Elevated Confidence",
                    "desc": "Improved skin condition, decreased weight, and insane freshness together create a stronger and more positive approach to life.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Tips",
            "subheading": "",
            "para": "After receiving women's health services, read these tips for your aftercare to get the most from treatment.",
            "items": [
                {
                    "title": "Get Regular Check Ups",
                    "desc": "Book follow-up appointments to see how you are doing.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Live a Healthy Lifestyle",
                    "desc": "Follow a balanced diet, get plenty of exercise, and avoid smoking or excessive drinking.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "Stay Empowered",
                    "desc": "Learn about women's health, and alert your doctor to any new concerns.",
                    "icon": "fa-solid fa-book-open-reader"
                },
                {
                    "title": "Reduce Stress",
                    "desc": "Learn about mindfulness, yoga, meditation like actions that can enhance your mental health.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Stick to Treatment Plans",
                    "desc": "Consistently take prescribed medications and therapies.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "Common questions about women's health services in Dubai",
            "items": [
                {
                    "question": "What is included in a women's health check-up in Dubai?",
                    "answer": "Women's health check-ups typically include screenings for cervical and breast health, hormonal evaluations, diagnostic imaging (e.g., ultrasounds, mammograms), and consultations with gynecologists."
                },
                {
                    "question": "How can ZepBound Mounjaro assist in weight loss?",
                    "answer": "ZepBound Mounjaro is a modern treatment that assists in regulating the appetite, bringing hormonal balance to the body, and promoting healthy weight loss for women who are battling obesity or other metabolic disorders."
                },
                {
                    "question": "Do women's health clinics in Dubai offer menopause treatments?",
                    "answer": "Yes, most clinics deliver complete care services during menopause, from hormone replacement therapy and lifestyle and diet counseling that aims to cure menopausal hot flashes, mood swings, or bone loss symptoms."
                },
                {
                    "question": "What infertility treatments does Dubai offer?",
                    "answer": "Dubai presents state-of-the-art infertility treatment, ranging from IVF to ICSI to ovulation induction, egg freezing, and other programs related to donor egg solutions for individual clients."
                },
                {
                    "question": "How important are preventive screenings to a woman's well-being?",
                    "answer": "Absolutely. In fact, most preventive screenings, which include PAP smears, mammograms, and bone density tests, are geared towards the prevention of serious issues and early diagnosis, thus aiding long-term prognosis."
                }
            ]
        }
    },
    "ar": {
        "url": "womens-health-services",
        "meta": {
            "title": "خدمات صحة المرأة في دبي | ايليت بودي هوم",
            "description": "رعاية صحية شاملة للنساء تشمل الرعاية النسائية، علاج الخصوبة، العلاج الهرموني، والفحوصات الوقائية في دبي.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/womens-health/",
            "og": {
                "title": "خدمات صحة المرأة في دبي | ايليت بودي هوم",
                "description": "حلول صحية شاملة للنساء في جميع مراحل الحياة",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Womens-Health-Services-Dubai.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "صحة المرأة"],
        "hero": {
            "title": "خدمات صحة المرأة في دبي",
            "subtitle": "رعاية شاملة لكل مرحلة من مراحل الحياة",
            "description": "تُعد صحة المرأة فرعًا أساسيًا من الطب يُركّز على الحالات الصحية الخاصة بالنساء، والتي تؤثر على صحتهن الجسدية والنفسية والتناسلية. صُممت خدمات صحة المرأة في دبي لتقديم رعاية طبية عالمية المستوى، تضمن نهجًا شاملاً لدعم وتعزيز الصحة."
        },
        "howItWorks": {
            "title": "ماذا نُقدم كخدمات صحة المرأة؟",
            "description": "تُقدم خدمات الرعاية الصحية للمرأة في دبي وفقًا لنهج طبي مخصص لكل مرأة، مما يضمن تخطيطًا فرديًا للرعاية الصحية يتناسب مع كل مرحلة من مراحل حياتها."
        },
        "procedureSteps": {
            "heading": "خطوات الفحص في خدمات صحة المرأة",
            "para": "تمر عملية الفحص في خدمات صحة المرأة بعدة خطوات لضمان التشخيص الدقيق والعلاج الفعّال:",
            "steps": [
                {
                    "title": "الاستشارة الأولية",
                    "desc": "يقوم الطبيب المختص بمراجعة التاريخ الطبي للمريضة خلال الزيارة الاولى ومناقشة المشاكل الصحية التي تعاني منها.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "الفحوصات التشخيصية",
                    "desc": "تشمل مجموعة من الفحوصات، مثل فحوصات الدم الشاملة، والتصوير بالموجات فوق الصوتية، ومسحة عنق الرحم، وتصوير الثدي بالأشعة السينية (الماموجرام)، وغيرها من الفحوصات الضرورية.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "خطة العلاج",
                    "desc": "يتم وضع خطة علاج مخصصة بناءً على نتائج الفحوصات، والتي قد تشمل الأدوية، أو تغييرات في نمط الحياة، أو إجراءات طبية مثل العلاج بالهرمونات البديلة أو الجراحة بالمنظار.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "المتابعة",
                    "desc": "تُحدد مواعيد متابعة دورية بعد تلقي العلاج لمراقبة استجابة الجسم له وضمان تحقيق أفضل النتائج الصحية.",
                    "icon": "fa-solid fa-heartbeat"
                }
            ]
        },
        "benefits": {
            "heading": "مزايا خدمات صحة المرأة في دبي",
            "subheading": "",
            "para": "توفر خدمات صحة المرأة العديد من الفوائد التي تضمن الرفاهية الجسدية والنفسية، ومن أهمها:",
            "items": [
                {
                    "title": "زيادة الطاقة",
                    "desc": "يساعد العلاج الهرموني في تعزيز مستويات الطاقة وتقليل الشعور بالإرهاق.",
                    "icon": "fa-solid fa-battery-full"
                },
                {
                    "title": "توازن الهرمونات",
                    "desc": "يساهم العلاج بالهرمونات البديلة والعلاج المتخصص في استعادة التوازن الهرموني، مما يخفف من أعراض انقطاع الطمث ومتلازمة تكيس المبايض وغيرها.",
                    "icon": "fa-solid fa-balance-scale"
                },
                {
                    "title": "إدارة الوزن",
                    "desc": "توفر البرامج المتقدمة حلولًا فعالة للتحكم في الشهية وتحقيق فقدان وزن صحي ومستدام.",
                    "icon": "fa-solid fa-weight"
                },
                {
                    "title": "زيادة فرص الحمل",
                    "desc": "تساعد تقنيات الخصوبة المتطورة على تحسين فرص الإنجاب بشكل كبير.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "تحسين صحة البشرة",
                    "desc": "يحفز العلاج عملية تجديد البشرة، ويساعد في تقليل حب الشباب ومنح البشرة مظهر أكثر نضارة وشبابًا.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "تعزيز الصحة النفسية",
                    "desc": "توفر الاستشارات الفردية والعلاج المتخصص دعمًا نفسيًا يساعد في التعامل مع التوتر، والقلق، والاكتئاب.",
                    "icon": "fa-solid fa-brain"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "تُقدم خدمات صحة المرأة في دبي لضمان شمولية الرعاية الصحية وإتاحتها لأكبر عدد ممكن من النساء.",
            "items": [
                {
                    "title": "اضطرابات الدورة الشهرية",
                    "desc": "النساء اللواتي يعانين من اضطرابات الدورة الشهرية أو أعراض متلازمة ما قبل الحيض الشديدة.",
                    "icon": "fa-solid fa-calendar-days"
                },
                {
                    "title": "مشاكل الخصوبة",
                    "desc": "النساء اللواتي يخططن للحمل أو يواجهن صعوبة في الإنجاب.",
                    "icon": "fa-solid fa-venus"
                },
                {
                    "title": "سن اليأس",
                    "desc": "النساء اللواتي يقتربن من سن اليأس أو يمررن بمرحلة انقطاع الطمث.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "الرعاية الوقائية",
                    "desc": "النساء اللواتي يحتجن إلى فحوصات دورية وإجراءات وقائية للحفاظ على صحتهن.",
                    "icon": "fa-solid fa-shield-heart"
                },
                {
                    "title": "الاختلال الهرموني",
                    "desc": "النساء اللواتي يبحثن عن علاج لإدارة الوزن أو تحقيق التوازن الهرموني.",
                    "icon": "fa-solid fa-atom"
                },
                {
                    "title": "رعاية الحمل",
                    "desc": "الرعاية المتخصصة للحوامل والأمهات الجدد لضمان صحة مثالية أثناء الحمل وبعد الولادة.",
                    "icon": "fa-solid fa-person-pregnant"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يُعد التحضير للفحوصات أو العلاج الخاص بصحة المرأة أمرًا بسيطًا، ويمكن تلخيصه في الخطوات التالية:",
            "items": [
                {
                    "title": "حجز الموعد",
                    "desc": "احجزي استشارتك في عيادة مرموقة في دبي لضمان توفر الخدمة.",
                    "icon": "fa-solid fa-calendar-plus"
                },
                {
                    "title": "التاريخ الطبي",
                    "desc": "كوني مستعدة لمشاركة تاريخك الصحي، وأدويتك، ومسببات الحساسية إن وجدت، والعمليات الجراحية السابقة.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الاستعداد قبل الفحوصات",
                    "desc": "قد يُطلب منك الصيام أو التوقف عن تناول بعض الأدوية قبل الفحوصات، حسب نوع الفحص المطلوب.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "الملابس المناسبة",
                    "desc": "ارتدي ملابس مريحة، خاصة في حال إجراء فحوصات مثل الموجات فوق الصوتية أو الفحوصات النسائية.",
                    "icon": "fa-solid fa-shirt"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "الاستفادة من خدمات صحة المرأة في دبي يمكن أن تُحدث تحولًا كبيرًا في حياتكِ. من بين أهم النتائج التي يمكنكِ تحقيقها:",
            "items": [
                {
                    "title": "اللياقة البدنية",
                    "desc": "تساعد برامج إنقاص الوزن في تحقيق نمط حياة صحي والوقاية من الأمراض المزمنة.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "توازن الهرمونات",
                    "desc": "يساعد علاج الاضطرابات الهرمونية على تقليل أعراض التقلبات المزاجية والهبّات الساخنة والإرهاق.",
                    "icon": "fa-solid fa-scale-balanced"
                },
                {
                    "title": "الكشف المبكر",
                    "desc": "تساعد الفحوصات الدورية في الكشف عن المشاكل الصحية المُحتَمَلَة، مما يتيح التدخل المبكر والعلاج الفعّال.",
                    "icon": "fa-solid fa-magnifying-glass"
                },
                {
                    "title": "تحسين الصحة الإنجابية",
                    "desc": "يرفع علاج الخصوبة من فرص الحمل ويضمن تجربة حمل أكثر صحة.",
                    "icon": "fa-solid fa-children"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "تعزز صحة البشرة، وفقدان الوزن، والاستقرار النفسي الشعور بالثقة وتُحسّن جودة الحياة بشكل عام.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح العناية بعد العلاج",
            "subheading": "",
            "para": "يُنصح باتباع هذه الإرشادات بعد العلاج لتحقيق أقصى استفادة من خدمات صحة المرأة:",
            "items": [
                {
                    "title": "المتابعة الدورية",
                    "desc": "احرصي على مواعيد الفحوصات الدورية لمراقبة تقدم حالتك الصحية.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "الحفاظ على نمط حياة صحي",
                    "desc": "تناولي غذاءً متوازنًا، ومارسي التمارين الرياضية بانتظام، وتجنبي التدخين أو الإفراط في استهلاك الكحول.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "الوعي الصحي",
                    "desc": "كوني على دراية بأحدث المعلومات حول صحة المرأة واستشيري طبيبك عند ظهور مستجدات صحية.",
                    "icon": "fa-solid fa-book-open-reader"
                },
                {
                    "title": "إدارة التوتر",
                    "desc": "مارسي التأمل أو اليوغا أو الأنشطة التي تساعد في تعزيز الصحة النفسية والتخلص من التوتر.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "الالتزام بخطة العلاج",
                    "desc": "اتبعي الإرشادات الطبية الموصوفة، سواء كانت أدوية أو علاج، لضمان نتائج فعالة وطويلة الأمد.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "subheading": "أسئلة شائعة حول خدمات صحة المرأة في دبي",
            "items": [
                {
                    "question": "ما الذي يشمله فحص صحة المرأة في دبي؟",
                    "answer": "يشمل الفحص الصحي للمرأة فحوصات للكشف عن صحة عنق الرحم والثدي، وفحص الهرمونات، وتصوير تشخيصي مثل التصوير بالأشعة فوق الصوتية وبالأشعة السينية (الماموجرام)، إلى جانب استشارات مع أطباء النساء والتوليد."
                },
                {
                    "question": "هل توفر عيادات صحة المرأة في دبي علاج لانقطاع الطمث؟",
                    "answer": "نعم، تقدم معظم العيادات رعاية متكاملة خلال فترة انقطاع الطمث، بما في ذلك العلاج بالهرمونات البديلة، والاستشارات حول نمط الحياة والتغذية، وذلك للتخفيف من الهبّات الساخنة، وتقلبات المزاج، وأعراض فقدان كثافة العظام."
                },
                {
                    "question": "ما هو العلاج المتوفر لمشكلة العقم في دبي؟",
                    "answer": "توفر دبي أحدث التقنيات لعلاج العقم، مثل التلقيح الإصطناعي، والحقن المجهري، وتحريض الإباضة، وتجميد البويضات، إلى جانب برامج التبرع بالبويضات المصممة لتلبية احتياجات كل مريضة على حدة."
                },
                {
                    "question": "ما مدى أهمية الفحوصات الوقائية لصحة المرأة؟",
                    "answer": "تعتبر الفحوصات الوقائية مثل مسحة عنق الرحم، والماموجرام، وفحص كثافة العظام ضرورية للكشف المبكر عن المشاكل الصحية، مما يسهم في الوقاية من الأمراض الخطيرة وتحسين فرص العلاج على المدى الطويل."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/New-Service-Banner-Image-3.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 127,
            "title": "Women's Health Services in Dubai",
            "description": "Comprehensive healthcare solutions for women at all life stages",
            "price": 0
        }
    }
}